import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bank, ClipboardText, UsersThree, Receipt, AddressBook, Warning, BellRinging } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, FunnelChart, Funnel, LabelList } from "recharts";
import api from "@/lib/api";
import { STAGE_LABELS, JOB_STATUS_LABELS, JOB_STATUS_COLORS } from "@/lib/constants";
import { Card } from "@/components/ui/card";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading dashboard…</div>;

  const kpis = [
    { icon: Bank, label: "Bank Branches", value: stats.total_branches, tone: "text-primary", to: "/app/branches" },
    { icon: AddressBook, label: "Client Master", value: stats.total_clients ?? 0, sub: "clients", tone: "text-indigo-600", to: "/app/clients" },
    { icon: ClipboardText, label: "Valuation Assignments", value: stats.active_jobs, sub: `${stats.completed_jobs} completed`, tone: "text-blue-600", to: "/app/jobs" },
    { icon: UsersThree, label: "Teams Activity", value: stats.team_actions_today ?? 0, sub: `${stats.team_members ?? 0} members · actions today`, tone: "text-emerald-600", to: "/app/team/log" },
    { icon: Receipt, label: "Accounts", value: `₹${(stats.outstanding || 0).toLocaleString("en-IN")}`, sub: "outstanding", tone: "text-accent", to: "/app/invoices" },
  ];

  const pipelineData = stats.pipeline.map((p) => ({ name: STAGE_LABELS[p.stage], value: p.count, key: p.stage }));
  const jobData = stats.job_stats.map((j) => ({ name: JOB_STATUS_LABELS[j.status], count: j.count }));
  const funnelColors = ["#94a3b8", "#3b82f6", "#f59e0b", "#059669", "#e11d48"];

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-accent">Overview</div>
        <h1 className="font-display font-black text-3xl md:text-4xl text-primary mt-1">Business Dashboard</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {kpis.map((k, i) => (
          <Card key={k.label} role="button" tabIndex={0} onClick={() => navigate(k.to)} onKeyDown={(e) => { if (e.key === "Enter") navigate(k.to); }} className="p-5 border-border cursor-pointer transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg stagger-in" style={{ animationDelay: `${i * 60}ms` }} data-testid={`kpi-${i}`}>
            <div className="flex items-start justify-between">
              <k.icon size={24} weight="duotone" className={k.tone} />
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground opacity-0 group-hover:opacity-100">View →</span>
            </div>
            <div className="mt-3 font-display font-black text-2xl md:text-3xl text-primary tabular">{k.value}</div>
            <div className="text-xs tracking-wide uppercase text-muted-foreground mt-0.5">{k.label}</div>
            {k.sub && <div className="text-xs text-muted-foreground mt-1">{k.sub}</div>}
            <div className="text-[10px] text-accent font-semibold mt-2">Click to explore →</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: "Invoiced", value: `₹${(stats.total_invoiced || 0).toLocaleString("en-IN")}`, tone: "text-primary", to: "/app/invoices" },
          { label: "Collected", value: `₹${(stats.total_collected || 0).toLocaleString("en-IN")}`, tone: "text-emerald-600", to: "/app/invoices/payments" },
          { label: "Outstanding", value: `₹${(stats.outstanding || 0).toLocaleString("en-IN")}`, tone: "text-amber-600", to: "/app/invoices/payments" },
        ].map((m, i) => (
          <Card key={m.label} role="button" tabIndex={0} onClick={() => navigate(m.to)} onKeyDown={(e) => { if (e.key === "Enter") navigate(m.to); }} className="p-4 border-border flex items-center justify-between cursor-pointer transition-colors duration-150 hover:bg-muted/50" data-testid={`invoice-kpi-${i}`}>
            <div className="text-xs tracking-wide uppercase text-muted-foreground">{m.label}</div>
            <div className={`font-display font-bold text-xl tabular ${m.tone}`}>{m.value}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border-border">
          <h3 className="font-display text-xl font-semibold text-primary">Lead Pipeline</h3>
          <p className="text-xs text-muted-foreground mb-4">Bank branches by stage</p>
          <ResponsiveContainer width="100%" height={240}>
            <FunnelChart>
              <Tooltip />
              <Funnel dataKey="value" data={pipelineData} isAnimationActive>
                {pipelineData.map((_, i) => <Cell key={i} fill={funnelColors[i % funnelColors.length]} />)}
                <LabelList position="right" fill="#1A202C" stroke="none" dataKey="name" className="text-xs" />
                <LabelList position="left" fill="#4A5568" stroke="none" dataKey="value" className="text-xs font-mono" />
              </Funnel>
            </FunnelChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6 border-border">
          <h3 className="font-display text-xl font-semibold text-primary">Job Status</h3>
          <p className="text-xs text-muted-foreground mb-4">Valuation jobs by stage</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={jobData} margin={{ left: -20 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: "rgba(10,37,64,0.05)" }} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} fill="#0A2540" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="p-6 border-border">
        <div className="flex items-center gap-2 mb-1"><ClipboardText size={20} weight="duotone" className="text-blue-600" /><h3 className="font-display text-xl font-semibold text-primary">Valuation Formats</h3></div>
        <p className="text-xs text-muted-foreground mb-4">Jobs by report format — your most common valuation types</p>
        {(() => {
          const fs = stats.format_stats || [];
          const total = fs.reduce((a, b) => a + (b.count || 0), 0);
          if (total === 0) return <p className="text-sm text-muted-foreground">No jobs assigned to a format yet.</p>;
          const max = Math.max(...fs.map((f) => f.count || 0), 1);
          return (
            <div className="space-y-3" data-testid="format-analytics">
              {fs.map((f) => (
                <div key={f.name} data-testid={`format-stat-${f.name}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium text-primary">{f.name}</span>
                    <span className="text-muted-foreground tabular">{f.count} job{f.count === 1 ? "" : "s"} · {total ? Math.round((f.count / total) * 100) : 0}%</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${((f.count || 0) / max) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border-border">
          <div className="flex items-center gap-2 mb-4"><Warning size={20} weight="fill" className="text-rose-500" /><h3 className="font-display text-xl font-semibold text-primary">Overdue Jobs</h3></div>
          {stats.overdue_jobs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No overdue jobs. Great work!</p>
          ) : (
            <div className="space-y-2" data-testid="overdue-list">
              {stats.overdue_jobs.slice(0, 6).map((j) => (
                <div key={j.id} className="flex items-center justify-between p-3 rounded-md bg-rose-50 border border-rose-100">
                  <div><div className="font-medium text-sm text-primary">{j.applicant_name}</div><div className="text-xs text-muted-foreground font-mono">{j.reference}</div></div>
                  <span className={`text-xs px-2 py-0.5 rounded border ${JOB_STATUS_COLORS[j.status]}`}>{JOB_STATUS_LABELS[j.status]}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="p-6 border-border">
          <div className="flex items-center gap-2 mb-4"><BellRinging size={20} weight="fill" className="text-accent" /><h3 className="font-display text-xl font-semibold text-primary">Pending Follow-ups</h3></div>
          {stats.pending_followups.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing pending.</p>
          ) : (
            <div className="space-y-2" data-testid="followups-list">
              {stats.pending_followups.slice(0, 6).map((f) => (
                <div key={f.id} className="flex items-center justify-between p-3 rounded-md bg-muted border border-border">
                  <div className="font-medium text-sm text-primary">{f.title}</div>
                  <div className="text-xs text-muted-foreground font-mono">{(f.due_date || "").slice(0, 10)}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
